/**
 * Adding, moving and cancelling a clinic appointment — QA report §3.3.
 *
 * Clinics reception works from the board: "+" on a clinic's column books into
 * it, a booking's pencil moves it, its ✕ cancels it. One dialog does both the
 * booking and the move, in the order the desk thinks in:
 *
 *   العيادة ← اليوم ← الميعاد ← المريض
 *
 * The times are chips, not a text box. Each says how many places it has left,
 * a full one cannot be pressed, and an evening slot the doctor added is marked —
 * so nobody has to know the clinic's grid, and nobody can type a time that is
 * not on it. The server checks all of it again under the clinic's lock
 * (Backend/lib/appointments.js), so a slot taken in the meantime comes back as
 * a sentence, not a double booking.
 *
 * The patient can be a hospital file (search, pick — name and phone fill in,
 * and the doctor opens that file from the appointment) or just a name and a
 * phone for somebody who has none yet.
 */

import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Checkbox, DateInput, Field, Icon, Input, Modal, Select, Spinner, Textarea } from '../../components/common';
import { operationsApi, patientsApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { todayKey } from '../../domain/dates';
import { formatDmy, ltr, twelveHour } from '../../domain/bidi';
import { formatMoney } from '../hospitalShared/labels';

export const APPOINTMENT_TYPES = [
  { id: 'exam', label: 'كشف جديد', icon: 'stethoscope' },
  { id: 'followup', label: 'متابعة', icon: 'repeat' },
];

/** The booking statuses in words, for the board and the dialogs. */
export const BOOKING_STATUS = {
  pending: 'بانتظار التأكيد',
  confirmed: 'مؤكد',
  in_progress: 'داخل الكشف',
  completed: 'انتهى',
  cancelled: 'ملغي',
};

/** Only a booking still waiting can be moved or cancelled (the server says the same). */
export const isEditable = (booking) => ['pending', 'confirmed'].includes(String(booking?.status || 'pending'));

const typeOf = (booking) => (booking?.bookingType === 'followup' || booking?.type === 'followup' ? 'followup' : 'exam');

/** Hospital files matching what the desk typed, a few at a time. */
function useFileSearch(q) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const text = q.trim();
    if (text.length < 2) {
      setRows([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setBusy(true);
      try {
        const result = await patientsApi.search({ q: text, limit: 6 });
        if (!cancelled) setRows(Array.isArray(result) ? result : []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [q]);
  return { rows, busy };
}

/**
 * @param {object[]} clinics      the board's clinics, each { clinic, doctor }
 * @param {object}   [booking]    the booking being moved; absent to book
 * @param {string}   [clinicId]   the column "+" was pressed on
 * @param {string}   [date]       the board's day
 */
export function AppointmentDialog({ open, onClose, clinics, booking = null, clinicId = '', date = '', onSaved }) {
  const { success, error: toastError } = useNotifications();
  const editing = Boolean(booking);
  const today = todayKey();

  const [form, setForm] = useState(null);
  const [slots, setSlots] = useState(null);
  const [slotsError, setSlotsError] = useState(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const found = useFileSearch(search);

  // A fresh form every time the dialog opens, from the booking or the column.
  useEffect(() => {
    if (!open) return;
    setForm(
      booking
        ? {
            clinicId: String(booking.clinicId),
            date: booking.date,
            time: booking.time,
            patientName: booking.patientName || '',
            patientPhone: booking.patientPhone || '',
            type: typeOf(booking),
            file: null,
            notify: true,
          }
        : {
            clinicId: String(clinicId || clinics[0]?.clinic.id || ''),
            date: date && date >= today ? date : today,
            time: '',
            patientName: '',
            patientPhone: '',
            type: 'exam',
            file: null,
            notify: true,
          },
    );
    setSearch('');
  }, [open, booking, clinicId, date, clinics, today]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // The chosen clinic's day, refetched when either changes.
  useEffect(() => {
    if (!open || !form?.clinicId || !form?.date) return undefined;
    let cancelled = false;
    setSlots(null);
    setSlotsError(null);
    operationsApi
      .appointmentSlots({ clinicId: form.clinicId, date: form.date, exclude: booking?.id })
      .then((data) => !cancelled && setSlots(data))
      .catch((err) => !cancelled && setSlotsError(err.message));
    return () => {
      cancelled = true;
    };
  }, [open, form?.clinicId, form?.date, booking?.id]);

  const chosen = clinics.find((c) => String(c.clinic.id) === String(form?.clinicId));
  const freeCount = useMemo(() => (slots?.slots || []).filter((s) => s.free > 0).length, [slots]);
  const isCurrent = (time) =>
    editing && String(form?.clinicId) === String(booking.clinicId) && form?.date === booking.date && time === booking.time;

  if (!open || !form) return null;

  const moved = editing && (String(form.clinicId) !== String(booking.clinicId) || form.date !== booking.date || form.time !== booking.time);
  const ready = form.clinicId && form.date && form.time && form.patientName.trim();

  const save = async () => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      const payload = {
        clinicId: form.clinicId,
        date: form.date,
        time: form.time,
        patientName: form.patientName,
        patientPhone: form.patientPhone,
        type: form.type,
        notify: form.notify,
      };
      if (editing) {
        const result = await operationsApi.updateAppointment(booking.id, payload);
        success(result.moved ? `تم نقل موعد ${form.patientName} إلى ${formatDmy(form.date)} ${form.time}` : 'تم حفظ التعديل');
      } else {
        await operationsApi.createAppointment({ ...payload, hospitalPatientId: form.file?.id ?? null });
        success(`تم حجز موعد ${form.patientName} — ${formatDmy(form.date)} ${form.time}`);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toastError(err.message);
      // A slot taken meanwhile: show the day as it is now.
      if (err.status === 409) {
        operationsApi.appointmentSlots({ clinicId: form.clinicId, date: form.date, exclude: booking?.id }).then(setSlots).catch(() => {});
      }
    } finally {
      setSaving(false);
    }
  };

  const price = slots?.prices?.[form.type];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `تعديل موعد ${booking.patientName}` : 'حجز موعد جديد في العيادة'}
      icon={editing ? 'calendar-clock' : 'calendar-plus'}
      size="lg"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-slate-400">
            {form.time ? (
              <>
                <Icon name="calendar-check" size={13} className="inline text-emerald-300" />{' '}
                {chosen?.clinic.name} — {ltr(formatDmy(form.date))} — {ltr(form.time)}
              </>
            ) : (
              'اختر ميعاداً من مواعيد العيادة'
            )}
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              رجوع
            </Button>
            <Button icon={editing ? 'save' : 'calendar-plus'} onClick={save} loading={saving} disabled={!ready}>
              {editing ? 'حفظ التعديل' : 'تأكيد الحجز'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {/* 1 — where and when */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
          <Field label="العيادة والطبيب" required>
            <Select value={form.clinicId} onChange={(e) => set({ clinicId: e.target.value, time: '' })}>
              {clinics.map(({ clinic, doctor }) => (
                <option key={clinic.id} value={clinic.id}>
                  {clinic.name}
                  {doctor?.name ? ` — ${doctor.name}` : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="اليوم" required>
            <DateInput value={form.date} min={today} clearable={false} onChange={(e) => e.target.value && set({ date: e.target.value, time: '' })} />
          </Field>
        </div>

        {/* 2 — the time, as the clinic's own slots */}
        <section>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-sm font-bold text-white">
              <Icon name="clock" size={16} className="text-cyan-300" /> الميعاد
            </h3>
            {slots && !slots.closed && (
              <span className="text-xs text-slate-400">
                {freeCount} ميعاد متاح · سعة الميعاد {slots.capacity}
              </span>
            )}
          </div>

          {slotsError ? (
            <p className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">{slotsError}</p>
          ) : !slots ? (
            <Spinner label="جارٍ تحميل مواعيد العيادة" variant="inline" />
          ) : slots.closed ? (
            <div className="slot-closed">
              <Icon name="calendar-x" size={22} />
              <span>{slots.reason} — اختر يوماً آخر.</span>
            </div>
          ) : (
            <>
              <div className="slot-grid" role="radiogroup" aria-label="اختر الميعاد">
                {slots.slots.map((slot, i) => {
                  const selected = form.time === slot.time;
                  const full = slot.free <= 0 && !selected;
                  return (
                    <button
                      key={slot.time}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={full}
                      onClick={() => set({ time: slot.time })}
                      className={['slot-chip', selected && 'slot-chip--on', full && 'slot-chip--full', slot.extra && 'slot-chip--extra']
                        .filter(Boolean)
                        .join(' ')}
                      style={{ animationDelay: `${Math.min(i, 24) * 18}ms` }}
                      title={twelveHour(slot.time)}
                    >
                      <span className="slot-chip__time" dir="ltr">
                        {slot.time}
                      </span>
                      <span className="slot-chip__left">
                        {isCurrent(slot.time) ? 'الموعد الحالي' : slot.free > 0 ? `${slot.free} متاح` : 'مكتمل'}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-400">
                <span className="flex items-center gap-1">
                  <i className="slot-legend slot-legend--free" /> متاح
                </span>
                <span className="flex items-center gap-1">
                  <i className="slot-legend slot-legend--full" /> مكتمل
                </span>
                <span className="flex items-center gap-1">
                  <i className="slot-legend slot-legend--extra" /> ساعات إضافية
                </span>
              </div>
              {slots.past && <p className="mt-2 text-xs text-amber-300">هذا اليوم مضى — لا يمكن الحجز فيه.</p>}
            </>
          )}
        </section>

        {/* 3 — who */}
        <section className="space-y-3">
          <h3 className="flex items-center gap-2 text-sm font-bold text-white">
            <Icon name="user-round" size={16} className="text-cyan-300" /> المريض
          </h3>

          {!editing && (
            <div className="relative">
              {form.file ? (
                <div className="flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3">
                  <Icon name="folder-check" size={18} className="text-emerald-300" />
                  <span className="min-w-0 flex-1 text-sm text-emerald-100">
                    مرتبط بملف المريض: <b className="text-white">{form.file.fullName}</b>
                  </span>
                  <Button variant="subtle" icon="x" onClick={() => set({ file: null })}>
                    فك الربط
                  </Button>
                </div>
              ) : (
                <>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="ابحث في ملفات المرضى بالاسم أو الهاتف أو الرقم القومي (اختياري)"
                    aria-label="البحث في ملفات المرضى"
                  />
                  {(found.busy || found.rows.length > 0) && (
                    <ul className="mt-2 divide-y divide-white/5 rounded-xl border border-white/10">
                      {found.busy && found.rows.length === 0 && <li className="p-3 text-xs text-slate-400">جارٍ البحث…</li>}
                      {found.rows.map((p) => (
                        <li key={p.id}>
                          <button
                            type="button"
                            className="flex w-full items-center gap-3 p-2.5 text-start transition-colors hover:bg-white/5"
                            onClick={() => {
                              set({ file: p, patientName: p.fullName || '', patientPhone: p.phonePrimary || '' });
                              setSearch('');
                            }}
                          >
                            <Icon name="folder-open" size={16} className="text-cyan-300" />
                            <span className="min-w-0 flex-1 truncate text-sm text-white">{p.fullName}</span>
                            <span className="text-xs text-slate-400" dir="ltr">
                              {p.phonePrimary || p.nationalId || ''}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="اسم المريض" required>
              <Input value={form.patientName} onChange={(e) => set({ patientName: e.target.value })} placeholder="الاسم كما سيظهر للطبيب" />
            </Field>
            <Field label="رقم الهاتف" hint="تُرسل عليه رسالة الموعد عبر واتساب">
              <Input dir="ltr" inputMode="tel" value={form.patientPhone} onChange={(e) => set({ patientPhone: e.target.value })} placeholder="01xxxxxxxxx" />
            </Field>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {APPOINTMENT_TYPES.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`chip-toggle ${form.type === t.id ? 'chip-toggle--on' : ''}`}
                aria-pressed={form.type === t.id}
                onClick={() => set({ type: t.id })}
              >
                <Icon name={t.icon} size={14} />
                {t.label}
                {slots?.prices && <span className="chip-toggle__count">{formatMoney(slots.prices[t.id])}</span>}
              </button>
            ))}
            {price !== undefined && editing && booking.depositPaid && (
              <span className="text-xs text-amber-300">دُفع عربون على هذا الموعد — يبقى سعره كما هو.</span>
            )}
          </div>
        </section>

        {(!editing || moved) && (
          <Checkbox
            checked={form.notify}
            onChange={(e) => set({ notify: e.target.checked })}
            label={editing ? 'أضف رسالة للمريض بالموعد الجديد إلى صندوق الإخطارات (واتساب)' : 'أضف رسالة تأكيد الحجز للمريض إلى صندوق الإخطارات (واتساب)'}
          />
        )}
      </div>
    </Modal>
  );
}

/** "Why?" and "tell them?", then the cancel. */
export function CancelAppointmentDialog({ booking, onClose, onCancelled }) {
  const { success, error: toastError } = useNotifications();
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setReason('');
    setNotify(true);
  }, [booking]);

  if (!booking) return null;

  const cancel = async () => {
    setBusy(true);
    try {
      await operationsApi.cancelAppointment(booking.id, { reason, notify });
      success(`تم إلغاء موعد ${booking.patientName}`);
      onCancelled?.();
      onClose();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="إلغاء الموعد"
      icon="calendar-x"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            رجوع
          </Button>
          <Button variant="danger" icon="calendar-x" onClick={cancel} loading={busy}>
            تأكيد الإلغاء
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-xl border border-red-500/25 bg-red-500/10 p-3 text-sm text-red-100">
          <p className="font-bold text-white">{booking.patientName}</p>
          <p className="mt-1 text-xs">
            {booking.clinicName || 'العيادة'} — {ltr(formatDmy(booking.date))} — {ltr(booking.time)}
          </p>
          <Badge className="mt-2 border-white/10 bg-white/5 text-slate-200">{BOOKING_STATUS[booking.status] || booking.status}</Badge>
        </div>
        <Field label="سبب الإلغاء" hint="يظهر للطبيب، ويُذكر في رسالة المريض">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="مثال: بطلب من المريض" maxLength={300} />
        </Field>
        <Checkbox checked={notify} onChange={(e) => setNotify(e.target.checked)} label="أضف رسالة الإلغاء للمريض إلى صندوق الإخطارات (واتساب)" />
      </div>
    </Modal>
  );
}

export default AppointmentDialog;
